import React, { useState, useEffect } from "react";
import {
  X,
  Clock,
  AlertCircle,
  CheckCircle,
  Info,
  Loader,
  Plane,
  Calendar,
  Users,
  CreditCard,
  Download,
  FileText,
  Ticket,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import baseURL from "../../../../Services/Config";
// Import the flight booking services WITH real-time status
import {
  fetchUpcomingFlightBookings,
  fetchAllFlightBookings,
  fetchFlightRealTimeStatus,
  getFlightUserType,
  isFlightUserAuthenticated,
} from "../../services/bookingApi";

const FlightBookingComponent = () => {
  const navigate = useNavigate();
  const [bookingType, setBookingType] = useState("upcoming");
  const [allBookings, setAllBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [userRole, setUserRole] = useState("user");

  // Real-time status popup state
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [statusError, setStatusError] = useState("");

  const [selectedBooking, setSelectedBooking] = useState(null);

  // Download states
  const [downloadingTaxInvoices, setDownloadingTaxInvoices] = useState(
    new Set()
  );
  const [downloadingETickets, setDownloadingETickets] = useState(new Set());

  // Get current bookings based on selected type
  const currentBookings =
    bookingType === "upcoming" ? upcomingBookings : allBookings;

  // Helper function to get auth token
  const getAuthToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken") ||
      localStorage.getItem("userToken") ||
      sessionStorage.getItem("token")
    );
  };

  // Create auth headers for PDF download
  const createAuthHeaders = () => {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing. Please login again.");
    }
    return {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
  };

  // Download Flight Tax Invoice - Improved Implementation
  const downloadFlightTaxInvoice = async (booking) => {
    const bookingId = booking.bookingId;

    if (!bookingId) {
      toast.error("Booking ID not found. Cannot download tax invoice.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    console.log(
      "📥 Starting Flight Tax Invoice download for booking:",
      bookingId
    );
    setDownloadingTaxInvoices((prev) => new Set(prev.add(bookingId)));

    try {
      // Create auth headers
      const headers = createAuthHeaders();
      console.log("🔐 Using auth token for download request");

      // Construct the API URL - Tax Invoice endpoint
      const apiUrl = `${baseURL}/airservice/download/invoice/flight/${bookingId}`;
      console.log("🚀 Flight Tax Invoice API URL:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: headers,
      });

      console.log(
        "📊 Flight Tax Invoice API Response Status:",
        response.status
      );
      console.log(
        "📋 Response Headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 401) {
          throw new Error("Authentication failed. Please login again.");
        }
        if (response.status === 404) {
          throw new Error("Tax invoice not found for this booking.");
        }
        if (response.status === 403) {
          throw new Error(
            "Access denied. You may not have permission to download this invoice."
          );
        }
        if (response.status === 500) {
          throw new Error("Server error. Please try again later.");
        }

        const errorText = await response.text();
        console.error("❌ Flight Tax Invoice API Error Response:", errorText);
        throw new Error(
          `API Error: ${response.status} - ${errorText || response.statusText}`
        );
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get("content-type");
      console.log("📄 Flight Tax Invoice Response Content Type:", contentType);

      if (!contentType || !contentType.includes("application/pdf")) {
        console.warn("⚠️ Expected PDF but got:", contentType);
        throw new Error("Response is not a PDF file");
      }

      // Get the blob from response
      const blob = await response.blob();
      console.log("💾 Blob size:", blob.size, "bytes");

      if (blob.size === 0) {
        throw new Error("Received empty PDF file");
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Flight_Tax_Invoice_${bookingId}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;

      // Add to DOM temporarily for download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("✅ Flight Tax Invoice downloaded successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      console.log(
        "✅ Flight Tax Invoice downloaded successfully for booking:",
        bookingId
      );
    } catch (error) {
      console.error("❌ Error downloading flight tax invoice:", error);

      if (
        error.message.includes("Authentication failed") ||
        error.message.includes("Access denied")
      ) {
        toast.error("🔐 Please login again to download tax invoice.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.message.includes("not found")) {
        toast.error("📄 Tax invoice not available for this booking.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.message.includes("Server error")) {
        toast.error(
          "🔧 Server is temporarily unavailable. Please try again later.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        toast.error(`❌ Error downloading tax invoice: ${error.message}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } finally {
      setDownloadingTaxInvoices((prev) => {
        const newSet = new Set(prev);
        newSet.delete(bookingId);
        return newSet;
      });
    }
  };

  // Download Flight E-Ticket - Improved Implementation
  const downloadFlightETicket = async (booking) => {
    const bookingId = booking.bookingId;

    if (!bookingId) {
      toast.error("Booking ID not found. Cannot download e-ticket.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    console.log("🎫 Starting Flight E-Ticket download for booking:", bookingId);
    setDownloadingETickets((prev) => new Set(prev.add(bookingId)));

    try {
      // Create auth headers
      const headers = createAuthHeaders();
      console.log("🔐 Using auth token for e-ticket download request");

      // Construct the API URL - E-Ticket endpoint
      const apiUrl = `${baseURL}/airservice/download/e-ticket/flight/${bookingId}`;
      console.log("🚀 Flight E-Ticket API URL:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: headers,
      });

      console.log("📊 Flight E-Ticket API Response Status:", response.status);
      console.log(
        "📋 Response Headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 401) {
          throw new Error("Authentication failed. Please login again.");
        }
        if (response.status === 404) {
          throw new Error("E-ticket not found for this booking.");
        }
        if (response.status === 403) {
          throw new Error(
            "Access denied. You may not have permission to download this e-ticket."
          );
        }
        if (response.status === 500) {
          throw new Error("Server error. Please try again later.");
        }

        const errorText = await response.text();
        console.error("❌ Flight E-Ticket API Error Response:", errorText);
        throw new Error(
          `API Error: ${response.status} - ${errorText || response.statusText}`
        );
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get("content-type");
      console.log("📄 Flight E-Ticket Response Content Type:", contentType);

      if (!contentType || !contentType.includes("application/pdf")) {
        console.warn("⚠️ Expected PDF but got:", contentType);
        throw new Error("Response is not a PDF file");
      }

      // Get the blob from response
      const blob = await response.blob();
      console.log("💾 Blob size:", blob.size, "bytes");

      if (blob.size === 0) {
        throw new Error("Received empty PDF file");
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Flight_E_Ticket_${bookingId}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;

      // Add to DOM temporarily for download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("✅ Flight E-Ticket downloaded successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      console.log(
        "✅ Flight E-Ticket downloaded successfully for booking:",
        bookingId
      );
    } catch (error) {
      console.error("❌ Error downloading flight e-ticket:", error);

      if (
        error.message.includes("Authentication failed") ||
        error.message.includes("Access denied")
      ) {
        toast.error("🔐 Please login again to download e-ticket.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.message.includes("not found")) {
        toast.error("🎫 E-ticket not available for this booking.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.message.includes("Server error")) {
        toast.error(
          "🔧 Server is temporarily unavailable. Please try again later.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        toast.error(`❌ Error downloading e-ticket: ${error.message}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } finally {
      setDownloadingETickets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(bookingId);
        return newSet;
      });
    }
  };

  // Transform API response to component-friendly format
  const transformBookingData = (apiBookings) => {
    if (!Array.isArray(apiBookings)) return [];

    return apiBookings.map((booking) => {
      const result = booking.Result || {};
      const segments = result.Segments?.[0] || [];
      const firstSegment = segments[0] || {};
      const lastSegment = segments[segments.length - 1] || {};
      const passenger = result.Passenger?.[0] || {};

      return {
        // 🔥 FIXED: Use outer bookingId instead of result.BookingId
        bookingId: booking.bookingId || result.BookingId, // Use outer bookingId first, fallback to Result.BookingId
        pnr: result.PNR,
        airline: firstSegment.Airline,
        airlineName: firstSegment.Airline?.AirlineName,
        flightNumber: firstSegment.Airline?.FlightNumber,
        origin: firstSegment.Origin,
        destination: lastSegment.Destination || firstSegment.Destination,
        from: firstSegment.Origin?.AirportCode,
        to: (lastSegment.Destination || firstSegment.Destination)?.AirportCode,
        fromCity: firstSegment.Origin?.CityName,
        toCity: (lastSegment.Destination || firstSegment.Destination)?.CityName,
        departureTime: firstSegment.Origin?.DepartTime,
        arrivalTime: (lastSegment.Destination || firstSegment.Destination)
          ?.ArrivalTime,
        cabinClass: firstSegment.CabinClass,
        class: firstSegment.CabinClass,
        passengers: result.Passenger,
        totalPassengers: result.Passenger?.length || 1,
        totalAmount: result.InvoiceAmount || result.Fare?.PublishedPrice,
        amount: result.InvoiceAmount || result.Fare?.PublishedPrice,
        status:
          result.CancelStatus === "none" ? "Confirmed" : result.CancelStatus,
        bookingStatus:
          result.CancelStatus === "none" ? "Confirmed" : result.CancelStatus,
        paymentStatus: "Completed",
        bookingDate: booking.createdAt,
        createdAt: booking.createdAt,
        confirmationNo: result.PNR,
        journeyType: result.JourneyType,
        isDomestic: result.IsDomestic,
        fareType: result.FareType,
        segments: result.Segments,
        invoiceNo: result.InvoiceNo,
        invoiceAmount: result.InvoiceAmount,
        airlineCode: firstSegment.Airline?.AirlineCode,

        // Add fields needed for download APIs and real-time status
        SearchTokenId: booking.SearchTokenId,
        UserIp: booking.UserIp,
        result: result, // Keep the full result object for API calls

        // 🔥 IMPORTANT: Store both IDs for debugging and API calls
        outerBookingId: booking.bookingId, // The correct one from API response outer level
        innerBookingId: result.BookingId, // The one from Result object

        // Add the complete booking object for API calls that might need it
        originalBooking: booking,
      };
    });
  };

  // Extract flight details from real-time status response
  const extractFlightDetails = (bookingData) => {
    // ✅ bookingData me already Result hai
    if (!bookingData?.Result) return null;

    const result = bookingData.Result; // ✅ Ab sahi hai
    const segments = result.Segments?.[0] || [];
    const firstSegment = segments[0] || {};
    const passenger = result.Passenger?.[0] || {};
    const fare = result.Fare || {};

    return {
      // Bas yahi fields return karo jo tumhare UI me chahiye
      bookingId: result.BookingId,
      pnr: result.PNR,
      journeyType: result.JourneyType === 1 ? "One Way" : "Round Trip",
      isDomestic: result.IsDomestic ? "Domestic" : "International",
      fareType: result.FareType,
      isRefundable: result.IsRefundable ? "Yes" : "No",

      airline: {
        code: firstSegment.Airline?.AirlineCode,
        name: firstSegment.Airline?.AirlineName,
        flightNumber: firstSegment.Airline?.FlightNumber,
      },

      route: {
        origin: {
          code: firstSegment.Origin?.AirportCode,
          name: firstSegment.Origin?.AirportName,
          city: firstSegment.Origin?.CityName,
          terminal: firstSegment.Origin?.Terminal,
        },
        destination: {
          code: firstSegment.Destination?.AirportCode,
          name: firstSegment.Destination?.AirportName,
          city: firstSegment.Destination?.CityName,
          terminal: firstSegment.Destination?.Terminal,
        },
      },

      schedule: {
        departure: firstSegment.Origin?.DepartTime,
        arrival: firstSegment.Destination?.ArrivalTime,
        duration: firstSegment.Duration,
      },

      passenger: {
        name: `${passenger.Title} ${passenger.FirstName} ${passenger.LastName}`,
        email: passenger.Email,
        phone: passenger.ContactNo,
        ticketNumber: passenger.TicketNumber,
        seatNumber:
          passenger.Seat?.[0]?.SeatNo ||
          passenger.Seat?.[0]?.Code ||
          "Not Assigned",
      },

      cabinClass: firstSegment.CabinClass,
      aircraft: firstSegment.Craft,

      baggage: {
        cabin: firstSegment.CabinBaggage,
        checkIn: firstSegment.CheckInBaggage,
      },
    };
  };
  // const extractFlightDetails = (apiResponse) => {
  //   if (!apiResponse?.Result) return null;

  //   const result = apiResponse.Result;
  //   const segments = result.Segments?.[0] || [];
  //   const firstSegment = segments[0] || {};
  //   const passenger = result.Passenger?.[0] || {};
  //   const fare = result.Fare || {};

  //   return {
  //     // Booking Information
  //     bookingId: result.BookingId,
  //     pnr: result.PNR,
  //     journeyType: result.JourneyType === 1 ? "One Way" : "Round Trip",
  //     isDomestic: result.IsDomestic ? "Domestic" : "International",
  //     fareType: result.FareType,
  //     isRefundable: result.IsRefundable ? "Yes" : "No",

  //     // Flight Information
  //     airline: {
  //       code: firstSegment.Airline?.AirlineCode,
  //       name: firstSegment.Airline?.AirlineName,
  //       flightNumber: firstSegment.Airline?.FlightNumber,
  //     },
  //     route: {
  //       origin: {
  //         code: firstSegment.Origin?.AirportCode,
  //         name: firstSegment.Origin?.AirportName,
  //         city: firstSegment.Origin?.CityName,
  //         terminal: firstSegment.Origin?.Terminal,
  //       },
  //       destination: {
  //         code: firstSegment.Destination?.AirportCode,
  //         name: firstSegment.Destination?.AirportName,
  //         city: firstSegment.Destination?.CityName,
  //         terminal: firstSegment.Destination?.Terminal,
  //       },
  //     },
  //     schedule: {
  //       departure: firstSegment.Origin?.DepartTime,
  //       arrival: firstSegment.Destination?.ArrivalTime,
  //       duration: firstSegment.Duration,
  //     },

  //     // Passenger Information
  //     passenger: {
  //       name: `${passenger.Title} ${passenger.FirstName} ${passenger.LastName}`,
  //       email: passenger.Email,
  //       phone: passenger.ContactNo,
  //       ticketNumber: passenger.TicketNumber,
  //     },

  //     // Fare Information
  //     fare: {
  //       baseFare: fare.BaseFare,
  //       tax: fare.Tax,
  //       otherCharges: fare.OtherCharges,
  //       publishedPrice: fare.PublishedPrice,
  //       totalAmount: result.InvoiceAmount,
  //     },

  //     // Additional Details
  //     cabinClass: firstSegment.CabinClass,
  //     aircraft: firstSegment.Craft,
  //     invoiceNo: result.InvoiceNo,
  //     invoiceDate: result.InvoiceCreatedOn,

  //     // Baggage and Seat Info
  //     baggage: {
  //       cabin: firstSegment.CabinBaggage,
  //       checkIn: firstSegment.CheckInBaggage,
  //     },
  //   };
  // };

  // Load flight bookings using the simplified service
  const loadFlightBookings = async () => {
    setLoading(true);
    setError("");

    try {
      console.log("🛫 Loading flight bookings using simplified service...");

      if (!isFlightUserAuthenticated()) {
        throw new Error("User is not authenticated. Please login again.");
      }

      const currentUserType = getFlightUserType();
      console.log("👤 Current user type:", currentUserType);

      // Fetch all bookings
      try {
        const allResponse = await fetchAllFlightBookings(currentUserType);
        console.log("📦 All bookings raw response:", allResponse);

        if (allResponse.success && allResponse.data) {
          let bookingsData = [];

          if (allResponse.data.data && Array.isArray(allResponse.data.data)) {
            bookingsData = allResponse.data.data;
          } else if (Array.isArray(allResponse.data)) {
            bookingsData = allResponse.data;
          }

          const transformedBookings = transformBookingData(bookingsData);
          const sortedAll = transformedBookings.sort((a, b) => {
            const dateA = new Date(a.bookingDate || a.createdAt);
            const dateB = new Date(b.bookingDate || b.createdAt);
            return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
          });

          setAllBookings(sortedAll);
        } else {
          setError(allResponse.message || "Failed to fetch all bookings");
        }
      } catch (err) {
        console.error("❌ Error fetching all bookings:", err);
        setError(err.message || "Failed to fetch all bookings");
      }

      // Fetch upcoming bookings
      try {
        const upcomingResponse = await fetchUpcomingFlightBookings(
          currentUserType
        );

        if (upcomingResponse.success && upcomingResponse.data) {
          let bookingsData = [];

          if (
            upcomingResponse.data.data &&
            Array.isArray(upcomingResponse.data.data)
          ) {
            bookingsData = upcomingResponse.data.data;
          } else if (Array.isArray(upcomingResponse.data)) {
            bookingsData = upcomingResponse.data;
          }

          const transformedBookings = transformBookingData(bookingsData);
          const now = new Date();
          const futureBookings = transformedBookings.filter((booking) => {
            const departureDate = new Date(booking.departureTime);
            return departureDate > now;
          });

          const sortedUpcoming = futureBookings.sort((a, b) => {
            const dateA = new Date(a.departureTime || a.bookingDate);
            const dateB = new Date(b.departureTime || b.bookingDate);
            return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
          });

          setUpcomingBookings(sortedUpcoming);
        }
      } catch (err) {
        console.error("❌ Error fetching upcoming bookings:", err);
      }
    } catch (err) {
      console.error("❌ Error loading flight bookings:", err);
      setError(
        err.message || "Failed to load flight bookings. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Toggle sort order
  const toggleSortOrder = () => {
    const newOrder = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(newOrder);
    loadFlightBookings();
  };

  // Load data on component mount
  useEffect(() => {
    const userType = getFlightUserType();
    setUserRole(userType || "customer");
    loadFlightBookings();
  }, [sortOrder]);

  // Real-time status API call with proper data extraction
  const fetchRealTimeStatus = async (booking) => {
    setStatusLoading(true);
    setStatusError("");
    setSelectedBooking(booking);
    setShowStatusPopup(true);

    try {
      console.log("🔄 Fetching flight real-time status...");

      const currentUserType = getFlightUserType();
      const bookingId =
        booking.outerBookingId || booking.bookingId || booking.BookingId;

      console.log("🎫 Looking for Flight Booking ID:", bookingId);

      // ✅ Sabhi bookings fetch karo
      const allBookingsResponse = await fetchAllFlightBookings(
        currentUserType,
        {
          maxPages: 5,
          pageSize: 100,
        }
      );

      console.log("✅ API Response Structure:", allBookingsResponse);

      if (!allBookingsResponse.success || !allBookingsResponse.data) {
        throw new Error("Failed to fetch flight bookings");
      }

      // ✅ Extract bookings array
      let bookingsArray =
        allBookingsResponse.data.data || allBookingsResponse.data;

      if (!Array.isArray(bookingsArray)) {
        bookingsArray = [bookingsArray];
      }

      console.log("📦 Total flight bookings fetched:", bookingsArray.length);

      // ✅ Specific booking dhundo
      const foundBooking = bookingsArray.find((b) => {
        const bId = b.bookingId || b.BookingId || b.Result?.BookingId;
        console.log(`🔍 Comparing: ${bId} === ${bookingId}`);
        return String(bId) === String(bookingId);
      });

      if (!foundBooking) {
        console.warn("⚠️ Booking not found in list");
        throw new Error(`Flight booking ID ${bookingId} not found`);
      }

      console.log("✅ Found specific flight booking:", foundBooking);

      // ✅ Extract flight details from the found booking
      const flightDetails = extractFlightDetails(foundBooking);

      if (!flightDetails) {
        throw new Error("Failed to extract flight details");
      }

      setStatusData({
        success: true,
        isEmpty: false,
        flightDetails: flightDetails,
        rawApiResponse: foundBooking,
      });
    } catch (error) {
      console.error("❌ Error fetching flight real-time status:", error);

      let errorMessage = "Failed to fetch real-time status";

      if (error.response) {
        const { status } = error.response;
        switch (status) {
          case 400:
            errorMessage =
              "Invalid booking data. Please check booking details.";
            break;
          case 401:
            errorMessage = "Authentication failed. Please login again.";
            break;
          case 404:
            errorMessage = "Flight booking not found in the system.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = `Request failed with status ${status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setStatusError(errorMessage);
    } finally {
      setStatusLoading(false);
    }
  };
  // const fetchRealTimeStatus = async (booking) => {
  //   setStatusLoading(true);
  //   setStatusError("");
  //   setSelectedBooking(booking);
  //   setShowStatusPopup(true);

  //   try {
  //     console.log("🔄 Fetching flight real-time status...");

  //     const currentUserType = getFlightUserType();
  //     // const result = await fetchFlightRealTimeStatus(booking, currentUserType);
  //     const result = await fetchAllFlightBookings(
  //       currentUserType,
  //       booking.outerBookingId
  //     );
  //     console.log("✅ API Response Structure:", result.data);

  //     console.log("✅ Flight real-time status result:", result);

  //     // if (result.success) {
  //     //   // Extract flight details from the response
  //     //   const flightDetails = extractFlightDetails(result.data);

  //     //   setStatusData({
  //     //     ...result,
  //     //     flightDetails: flightDetails,
  //     //     rawApiResponse: result.data, // Keep raw response for debugging
  //     //   });
  //     if (result.success && result.data) {
  //       // ✅ Extract booking array first
  //       let bookingsArray = result.data.data || result.data;

  //       if (!bookingsArray || bookingsArray.length === 0) {
  //         setStatusData({ success: true, isEmpty: true });
  //         return;
  //       }

  //       // ✅ Get first booking
  //       const bookingData = bookingsArray[0];

  //       // ✅ Pass Result object to extract function
  //       const flightDetails = extractFlightDetails(bookingData);

  //       setStatusData({
  //         success: true,
  //         isEmpty: false,
  //         flightDetails: flightDetails,
  //         rawApiResponse: bookingData,
  //       });
  //     } else {
  //       throw new Error(result.error || "Failed to fetch status");
  //     }
  //   } catch (error) {
  //     console.error("❌ Error fetching flight real-time status:", error);

  //     let errorMessage = "Failed to fetch real-time status";

  //     if (error.response) {
  //       const { status } = error.response;
  //       switch (status) {
  //         case 400:
  //           errorMessage =
  //             "Invalid booking data. Please check booking details.";
  //           break;
  //         case 401:
  //           errorMessage = "Authentication failed. Please login again.";
  //           break;
  //         case 404:
  //           errorMessage = "Flight booking not found in the system.";
  //           break;
  //         case 500:
  //           errorMessage = "Server error. Please try again later.";
  //           break;
  //         default:
  //           errorMessage = `Request failed with status ${status}`;
  //       }
  //     } else if (error.message) {
  //       errorMessage = error.message;
  //     }

  //     setStatusError(errorMessage);
  //   } finally {
  //     setStatusLoading(false);
  //   }
  // };

  // // Close status popup
  const closeStatusPopup = () => {
    setShowStatusPopup(false);
    setStatusData(null);
    setStatusError("");
    setSelectedBooking(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format amount
  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  // Calculate flight duration
  const calculateDuration = (departure, arrival) => {
    if (!departure || !arrival) return "N/A";
    const diff = new Date(arrival) - new Date(departure);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Get status with basic styling
  const getBookingStatus = (booking) => {
    const status = booking.status || booking.bookingStatus || "Confirmed";
    let className = "text-red-600";

    if (status.toLowerCase() === "cancelled") className = "text-red-600";
    else if (status.toLowerCase() === "pending") className = "text-orange-600";

    return { status, className };
  };

  // Get journey type display
  const getJourneyType = (journeyType) => {
    switch (journeyType) {
      case 1:
        return "One Way";
      case 2:
        return "Round Trip";
      default:
        return "One Way";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h5 className="text-2xl font-bold text-gray-800">
            My Flight Bookings
          </h5>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 w-5 h-5 mr-2" />
            <div>
              <h3 className="text-red-800 font-medium">
                Error Loading Flight Bookings
              </h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={loadFlightBookings}
                className="mt-2 px-3 py-1 bg-brand-600 text-white text-sm rounded hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Type Tabs */}
      <div className="flex w-full mb-6 bg-gray-50 p-2 shadow-sm">
        <button
          className={`flex-1 text-center rounded-lg transition-all duration-300 font-semibold text-sm px-6 py-2
            ${
              bookingType === "upcoming"
                ? "bg-brand-600 text-white shadow-sm"
                : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-white/70"
            }
          `}
          onClick={() => setBookingType("upcoming")}
        >
          Upcoming Flights
          {upcomingBookings.length > 0 && (
            <span
              className={`ml-2 px-2.5 py-1 text-xs rounded-full font-bold
              ${
                bookingType === "upcoming"
                  ? "bg-white text-red-500"
                  : "bg-red-100 text-red-600"
              }
            `}
            >
              {upcomingBookings.length}
            </span>
          )}
        </button>

        <button
          className={`flex-1 text-center px-6 py-2 rounded-lg transition-all duration-300 font-semibold text-sm
            ${
              bookingType === "all"
                ? "bg-brand-600 text-white shadow-md"
                : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-white/70"
            }
          `}
          onClick={() => setBookingType("all")}
        >
          All Flights
          {allBookings.length > 0 && (
            <span
              className={`ml-2 px-2.5 py-1 text-xs rounded-full font-bold
              ${
                bookingType === "all"
                  ? "bg-white text-red-500"
                  : "bg-red-100 text-red-600"
              }
            `}
            >
              {allBookings.length}
            </span>
          )}
        </button>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading flight bookings...</span>
        </div>
      )}

      {/* Flight Bookings Table */}
      {!loading && (
        <div className="bg-white rounded border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Booking ID
                  </th>
                  {/* <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Airline & Flight
                  </th> */}
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Route
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Departure
                  </th>
                  {/* <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Arrival
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Passengers
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Class
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Journey Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    PNR
                  </th> */}
                  {/* <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Status
                  </th> */}
                  {/* <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Total Amount
                  </th> */}
                  {/* <th
                    className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-200"
                    onClick={toggleSortOrder}
                    title="Click to change sort order"
                  >
                    Booked On {sortOrder === "desc" ? "↓" : "↑"}
                  </th> */}
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Details
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-50">
                {currentBookings.length === 0 ? (
                  <tr>
                    <td colSpan="15" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-lg font-medium text-gray-600">
                          No flight bookings found
                        </span>
                        <span className="text-sm text-gray-400 mt-1">
                          {bookingType === "upcoming"
                            ? "Your upcoming flights will appear here"
                            : "Your flight booking history will appear here"}
                        </span>
                        <button
                          onClick={loadFlightBookings}
                          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Refresh Bookings
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentBookings.map((booking, index) => {
                    const bookingStatus = getBookingStatus(booking);
                    const isDownloadingTaxInvoice = downloadingTaxInvoices.has(
                      booking.bookingId
                    );
                    const isDownloadingETicket = downloadingETickets.has(
                      booking.bookingId
                    );

                    return (
                      <tr
                        key={booking.bookingId || index}
                        className="border-b border-gray-200 hover:bg-gray-100"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                          {booking.bookingId || booking.pnr}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          <div
                            className="max-w-xs truncate"
                            title={`${booking.airlineName} ${booking.flightNumber}`}
                          >
                            {booking.airlineName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.fromCity} - {booking.toCity}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">
                            {booking.from} → {booking.to}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.fromCity} - {booking.toCity}
                          </div>
                        </td>
                        {/* <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">
                            {formatTime(booking.departureTime)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(booking.departureTime)}
                          </div>
                        </td> */}
                        {/* <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">
                            {formatTime(booking.arrivalTime)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(booking.arrivalTime)}
                          </div>
                        </td> */}
                        {/* <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {calculateDuration(
                            booking.departureTime,
                            booking.arrivalTime
                          )}
                        </td> */}
                        {/* <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">
                            {booking.totalPassengers}
                          </div> */}
                        {/* <div className="text-xs text-gray-500">
                            {booking.totalPassengers === 1
                              ? "passenger"
                              : "passengers"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div
                            className="max-w-xs truncate"
                            title={booking.class}
                          >
                            {booking.class || "Economy"}
                          </div>
                        </td> */}
                        {/* <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="max-w-xs truncate">
                            {getJourneyType(booking.journeyType)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.isDomestic ? "Domestic" : "International"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                          {booking.pnr || "N/A"}
                        </td> */}

                        {/* <td className="px-4 py-3 text-sm">
                          <span
                            className={`font-medium ${bookingStatus.className}`}
                          >
                            {bookingStatus.status}
                          </span>
                        </td> */}

                        {/* <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {formatAmount(booking.totalAmount)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {formatDate(booking.bookingDate)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {sortOrder === "desc"
                                ? "Latest First"
                                : "Oldest First"}
                            </span>
                          </div>

                        </td> */}
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => fetchRealTimeStatus(booking)}
                            className="text-xs text-green-500 hover:text-green-700 font-semibold transition-colors"
                          >
                            View
                          </button>
                        </td>

                        {/* Downloads Column - Clean Implementation */}

                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            {/* Tax Invoice Download Button */}
                            <button
                              onClick={() => downloadFlightTaxInvoice(booking)}
                              disabled={downloadingTaxInvoices.has(
                                booking.bookingId
                              )}
                              className={`px-3 py-1 rounded text-white text-xs font-medium transition-colors ${
                                downloadingTaxInvoices.has(booking.bookingId)
                                  ? "bg-orange-500 cursor-wait"
                                  : "bg-brand-600 hover:bg-brand-600 active:bg-brand-700"
                              }`}
                              title="Download Flight Tax Invoice"
                            >
                              {downloadingTaxInvoices.has(booking.bookingId) ? (
                                <span className="flex items-center gap-1">
                                  <svg
                                    className="animate-spin h-3 w-3"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Downloading...
                                </span>
                              ) : (
                                "Tax Invoice"
                              )}
                            </button>

                            {/* E-Ticket Download Button */}
                            <button
                              onClick={() => downloadFlightETicket(booking)}
                              disabled={downloadingETickets.has(
                                booking.bookingId
                              )}
                              className={`px-3 py-1 rounded text-white text-xs font-medium transition-colors ${
                                downloadingETickets.has(booking.bookingId)
                                  ? "bg-orange-500 cursor-wait"
                                  : "bg-green-500 hover:bg-green-600 active:bg-green-700"
                              }`}
                              title="Download Flight E-Ticket"
                            >
                              {downloadingETickets.has(booking.bookingId) ? (
                                <span className="flex items-center gap-1">
                                  <svg
                                    className="animate-spin h-3 w-3"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Downloading...
                                </span>
                              ) : (
                                "E-Ticket"
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Real-time Status Popup with HIGHEST z-index to appear above all elements including sidebar */}
      {showStatusPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg shadow-lg max-w-5xl w-full mx-auto p-6 overflow-y-auto max-h-[85vh]">
            {/* Popup Header */}
            <div className="flex justify-between items-center bg-gradient-to-r from-blue-100 to-indigo-100 p-3 rounded-t-lg border-b mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Flight Status Details
                </h2>
                {selectedBooking && (
                  <p className="text-sm text-gray-600">
                    {selectedBooking.airlineName} {selectedBooking.flightNumber}{" "}
                    • {selectedBooking.from} → {selectedBooking.to}
                  </p>
                )}
              </div>
              <button
                onClick={closeStatusPopup}
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* Popup Content */}
            <div className="space-y-6">
              {statusLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin w-8 h-8 text-blue-500" />
                  <span className="ml-3 text-gray-600">
                    Fetching real-time flight status...
                  </span>
                </div>
              ) : statusError ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    Error Loading Flight Status
                  </h4>
                  <p className="text-gray-600 mb-4">{statusError}</p>
                  <button
                    onClick={() => fetchRealTimeStatus(selectedBooking)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  >
                    Try Again
                  </button>
                </div>
              ) : statusData?.flightDetails ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Booking Info */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h5 className="font-semibold text-gray-900 mb-3">
                      Booking Information
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">PNR Number:</span>
                        <span className="font-medium font-mono">
                          {statusData.flightDetails.pnr}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Journey Type:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.journeyType}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Route Type:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.isDomestic}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600">Refundable:</span>
                        <span
                          className={`font-medium ${
                            statusData.flightDetails.isRefundable === "Yes"
                              ? "text-emerald-600"
                              : "text-red-600"
                          }`}
                        >
                          {statusData.flightDetails.isRefundable}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Flight Details */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h5 className="font-semibold text-gray-900 mb-3">
                      Flight Details
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Airline:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.airline.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Flight Number:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.airline.code}{" "}
                          {statusData.flightDetails.airline.flightNumber}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Aircraft:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.aircraft || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cabin Class:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.cabinClass}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cabin Baggage:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.baggage.cabin || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Check-in Baggage:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.baggage.checkIn || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Route Info */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5 md:col-span-2">
                    <h5 className="font-semibold text-gray-900 mb-3">
                      Route Information
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h6 className="font-medium text-gray-800 mb-2">
                          Departure
                        </h6>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Airport:</span>{" "}
                            <span className="font-medium">
                              {statusData.flightDetails.route.origin.code} -{" "}
                              {statusData.flightDetails.route.origin.name}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">City:</span>{" "}
                            <span className="font-medium">
                              {statusData.flightDetails.route.origin.city}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Terminal:</span>{" "}
                            <span className="font-medium">
                              {statusData.flightDetails.route.origin.terminal ||
                                "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Time:</span>{" "}
                            <span className="font-medium">
                              {formatDate(
                                statusData.flightDetails.schedule.departure
                              )}{" "}
                              at{" "}
                              {formatTime(
                                statusData.flightDetails.schedule.departure
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h6 className="font-medium text-gray-800 mb-2">
                          Arrival
                        </h6>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Airport:</span>{" "}
                            <span className="font-medium">
                              {statusData.flightDetails.route.destination.code}{" "}
                              -{" "}
                              {statusData.flightDetails.route.destination.name}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">City:</span>{" "}
                            <span className="font-medium">
                              {statusData.flightDetails.route.destination.city}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Terminal:</span>{" "}
                            <span className="font-medium">
                              {statusData.flightDetails.route.destination
                                .terminal || "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Time:</span>{" "}
                            <span className="font-medium">
                              {formatDate(
                                statusData.flightDetails.schedule.arrival
                              )}{" "}
                              at{" "}
                              {formatTime(
                                statusData.flightDetails.schedule.arrival
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between pt-4 border-t border-gray-100 md:col-span-2">
                        <span className="text-gray-600">Flight Duration:</span>
                        <span className="font-medium">
                          {statusData.flightDetails.schedule.duration} minutes
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Passenger Info */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5 md:col-span-2">
                    <h5 className="font-semibold text-gray-900 mb-3">
                      Passenger Information
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>{" "}
                        <span className="font-medium">
                          {statusData.flightDetails.passenger.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>{" "}
                        <span className="font-medium">
                          {statusData.flightDetails.passenger.email}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>{" "}
                        <span className="font-medium">
                          {statusData.flightDetails.passenger.phone}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ticket Number:</span>{" "}
                        <span className="font-medium font-mono">
                          {statusData.flightDetails.passenger.ticketNumber}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600">Seat Number:</span>{" "}
                        <span className="font-medium font-mono">
                          {statusData.flightDetails.passenger.seatNumber}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-5 md:col-span-2">
                    <h5 className="font-medium text-blue-900 mb-3">
                      Why might this happen?
                    </h5>
                    <div className="text-sm text-blue-800 space-y-2">
                      <div>
                        • Real-time tracking may not be available for this
                        specific flight
                      </div>
                      <div>
                        • The airline might not provide live status updates
                      </div>
                      <div>
                        • Booking is too recent or too old for real-time
                        tracking
                      </div>
                      <div>• Flight is not yet in the tracking system</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    No Status Data
                  </h4>
                  <p className="text-gray-600">
                    Click the Status button to fetch real-time flight
                    information
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightBookingComponent;
